/** @jsx isolateComponent */

import Imgix from 'react-imgix';
import { SyntheticEvent } from 'react';
import { Interpolation } from '@emotion/core';

import { Animation, ShadowName, Shadows } from '../../../theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

export type Props = {
  /** Specifies alternative text for the image */
  alt: string;

  /** Inform screen-readers that they should ignore the image */
  ariaHidden?: boolean;

  /**
   * Only supported by Imgix URLs. Provide an aspect ratio as a string, in the
   * form of `width`:`height` e.g. "16:9". Imgix crop the image to fulfill the
   * desired aspect ratio.
   */
  aspectRatio?: string;

  /**
   * The maximum height of the image in pixels. If both `height` and `width`
   * are set, and the image is an Imgix URL, the image will be center-cropped
   * by the Imgix service.
   */
  height?: number;

  /**
   * @deprecated - Previously used by `Img` for the `react-lazyload` library.
   * We are now using a native browser-level lazyload so this no longer does
   * anything.
   *
   * The offset in pixels at which you wish the image lazyloader to trigger.
   * For example, if this is set to `100`, it will load the image when it is
   * 100px away from appearing at the the bottom of the viewport.
   * Also accepts negative numbers, or an array of numbers which will trigger
   * both the top-edge and bottom-edge offset.
   * If you provide a value of `null` value, you will opt-out of lazy loading.
   */
  lazyloadOffset?: number | number[] | null;

  /**
   * @deprecated - Previously used by `Img` for the `react-lazyload` library.
   * We are now using a native browser-level lazyload so this no longer does
   * anything.
   *
   * Will properly load content that is initially overflowed outside of parent
   * container (see caveats in docs: https://www.npmjs.com/package/react-lazyload#overflow)
   */
  lazyloadOverflow?: boolean;

  /**
   * @deprecated - Previously used by `Img` for the `react-lazyload` library.
   * We are now using a native browser-level lazyload so this no longer does
   * anything.
   *
   * Enables 300ms throttling of the lazy-loading mechanism.
   */
  lazyloadThrottle?: boolean;

  /** Toggles display of a border radius on the image. */
  hasRadius?: boolean;

  /** Optional override of border-radius style, only applies if `hasRadius` is true. */
  borderRadius?: string | number;

  /**
   * The CSS object-fit property, which is necessary to tweak image sizing in
   * some situations. This is useful to leverage imgix-powered performant
   * images in situations where a background image would otherwise be required.
   */
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';

  /** The CSS object-position property, works in conjunction with the
   * object-fit property to position the image as desired.
   * */
  objectPosition?: string;

  /**
   * Called when image fails to load
   */
  onError?: (a: SyntheticEvent<HTMLImageElement>) => void;

  /**
   * Called when image loads
   */
  onLoad?: (a: SyntheticEvent<HTMLImageElement>) => void;

  /** Applies a `box-shadow` with the specified shadow. */
  shadow?: ShadowName;

  /**
   * A comma separated list of source sizes. Each source size contains a media
   * query condition and the source size value. With this information, the
   * client will select one of the image sources available from the `srcset`
   * attribute.
   * The Img component uses `react-imgix` to automatically generate the `srcset`
   * list. The `srcset` list contains a comma separated list indicating possible
   * image sources the client can use.
   */
  sizes?: string;

  /** Source URL of the image */
  src: string;

  /**
   * The maximum width of the image in pixels. If both `height` and `width`
   * are set, and the image is an Imgix URL, the image will be center-cropped
   * by the Imgix service.
   */
  width?: number;

  /**
   * The lazy-load setting for the image component. This prop enables the
   * browser-level lazy loading API.
   *
   * - `lazy`: Defer loading of the resource until it reaches a calculated
   * distance from the viewport.
   * - `eager`: Load the resource immediately, regardless of where it's located
   * on the page.
   *
   * For more information about this API see https://web.dev/browser-level-image-lazy-loading/
   */
  loading?: 'eager' | 'lazy';
};

const Styles = labelStyles('Img', {
  root: (props: Props) => [
    {
      all: 'unset',
      boxSizing: 'border-box',
      borderRadius: 0,
      transitionProperty: 'box-shadow',
      transitionTimingFunction: Animation.transitionTimingFunction,
      transitionDuration: Animation.transitionDuration,
      // `width: 100%` is being added because the overwhelmingly common use
      // case for the Img component is to require it to fill its parent
      // container. Without this, consumers of Img are unable to force it to be
      // full width without stretching its aspect ratio. The inverse remains
      // possible: specifying a width, or wrapping the image in a container of
      // fixed width, can be used to limit the image size.
      width: '100%',
    },
    props.hasRadius &&
      withModifier('hasRadius', {
        borderRadius: props.borderRadius,
      }),
    props.shadow &&
      withModifier('hasShadow', {
        boxShadow: Shadows[props.shadow],
      }),
    props.objectFit &&
      withModifier('objectFit', {
        objectFit: props.objectFit,
      }),
    props.objectPosition &&
      withModifier('objectPosition', {
        objectPosition: props.objectPosition,
      }),
    props.width &&
      withModifier('width', {
        width: props.width,
      }),
    props.height &&
      withModifier('height', {
        height: props.height,
      }),
  ],
});

/**
 * Renders an `<img>` tag.
 *
 * Exposes additional features like lazy-loading, server-side cropping and
 * resizing, and responsive image support.
 */
const Img = (props: Props) => {
  const {
    alt,
    ariaHidden,
    aspectRatio,
    height,
    loading,
    onError,
    onLoad,
    sizes,
    src,
    width,
  } = props;

  return (
    <Imgix
      css={Styles.root(props) as Interpolation}
      data-testid="img"
      height={height}
      htmlAttributes={{
        alt,
        'aria-hidden': ariaHidden,
        onError,
        onLoad,
        loading,
      }}
      imgixParams={{
        ar: aspectRatio,
        // v9.x of react-imgix implements the default behavior of the Imgix API
        // which is to use `fit=clip`. This LOC re-introduces the v8.x behavior
        // which previously attached `fit=crop` whenever aspect ratio, height,
        // or width properties were applied.
        // See https://github.com/imgix/react-imgix#8x-to-90
        fit: aspectRatio || width || height || sizes ? 'crop' : undefined,
      }}
      sizes={sizes}
      src={src}
      width={width}
    />
  );
};

Img.defaultProps = {
  hasRadius: true,
  borderRadius: 10,
  loading: 'lazy',
};
export { Img };
